import type { ReactElement } from "react";
import { createBrowserRouter, Outlet, type RouteObject } from "react-router";
import browserHistory from "../helpers/browserHistory";
import AuthenticatedBoundary from "./AuthenticatedBoundary";
import RouterAppShell from "./RouterAppShell";
import RouterErrorElement from "./RouterErrorElement";
import RouterRouteElement from "./RouterRouteElement";
import { type AppRoute, authenticatedRoutes, unauthenticatedRoutes } from "./routeManifest";

function AuthenticatedLayout(): ReactElement {
  return (
    <AuthenticatedBoundary>
      <Outlet />
    </AuthenticatedBoundary>
  );
}

export function makeRouteObject(route: AppRoute): RouteObject {
  const common = {
    element: <RouterRouteElement route={route} />,
    handle: { appRoute: route },
    id: route.id,
  };

  if (!route.path) {
    return { ...common, path: "*" };
  }
  if (route.path === "/") {
    return { ...common, index: true };
  }
  return { ...common, path: route.path.replace(/^\//, "") };
}

export const appRouteObjects: RouteObject[] = [
  {
    children: [
      ...unauthenticatedRoutes.map(makeRouteObject),
      {
        children: authenticatedRoutes.map(makeRouteObject),
        element: <AuthenticatedLayout />,
        id: "authenticated-layout",
      },
    ],
    element: <RouterAppShell />,
    errorElement: <RouterErrorElement />,
    id: "root",
    path: "/",
  },
];

// Every Router 7 future behavior was enabled and verified on Router 6. They became
// defaults in Router 7 and remain the only behavior in Router 8, so passing removed
// v7 flags would retain obsolete migration configuration instead of exercising the
// installed runtime.
export const appRouter = createBrowserRouter(appRouteObjects);

export function configureNavigationFacade(navigator: { navigate(path: string): unknown }): void {
  browserHistory.set({
    push(path: string) {
      void navigator.navigate(path);
    },
  });
}

// Redux actions may navigate from descendant mount effects. Configure the
// application-owned facade before RouterProvider renders any route element.
configureNavigationFacade(appRouter);
